import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X, ChevronLeft, ChevronRight, Calendar, Video, Target, FileText, ExternalLink
} from 'lucide-react';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Card } from './ui/Card';
import type { AssignmentRow, LiveSessionRow } from '../types/sinif';

interface WeeklyCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  assignments: AssignmentRow[];
  liveSessions?: LiveSessionRow[];
  classId?: string;
  isTeacher?: boolean;
}

export function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export function getDaysOfWeek(monday: Date): Date[] {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push(d);
  }
  return days;
}

const DAY_NAMES = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

export function WeeklyCalendarModal({
  isOpen,
  onClose,
  assignments,
  liveSessions = [],
  classId,
}: WeeklyCalendarModalProps) {
  const [currentMonday, setCurrentMonday] = useState<Date>(() => getMonday(new Date()));

  if (!isOpen) return null;

  const days = getDaysOfWeek(currentMonday);
  const todayStr = new Date().toISOString().slice(0, 10);

  const sunday = days[6];
  const weekRangeLabel = `${days[0].toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} – ${sunday.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  function handlePrevWeek() {
    const prev = new Date(currentMonday);
    prev.setDate(prev.getDate() - 7);
    setCurrentMonday(prev);
  }

  function handleNextWeek() {
    const next = new Date(currentMonday);
    next.setDate(next.getDate() + 7);
    setCurrentMonday(next);
  }

  function handleThisWeek() {
    setCurrentMonday(getMonday(new Date()));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-surface border border-border rounded-3xl w-full max-w-6xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        
        {/* Modal Header */}
        <div className="p-5 border-b border-border flex items-center justify-between gap-4 flex-wrap bg-surface">
          <div className="flex items-center gap-3">
            <Calendar size={24} className="text-accent-500 shrink-0" />
            <div>
              <h2 className="text-lg font-black text-text-primary" style={{ fontFamily: 'var(--font-heading)' }}>
                Haftalık Ders & Ödev Takvimi
              </h2>
              <p className="text-xs text-text-muted mt-0.5">{weekRangeLabel}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrevWeek} title="Önceki Hafta">
              <ChevronLeft size={16} />
            </Button>
            <Button variant="outline" size="sm" onClick={handleThisWeek} className="text-xs font-bold">
              Bu Hafta
            </Button>
            <Button variant="outline" size="sm" onClick={handleNextWeek} title="Sonraki Hafta">
              <ChevronRight size={16} />
            </Button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-text-muted hover:text-text-primary hover:bg-border/40 transition-colors ml-2"
              aria-label="Kapat"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Content Grid */}
        <div className="p-4 overflow-y-auto flex-1 bg-surface/50">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
            {days.map((dayDate, idx) => {
              const dateStr = dayDate.toISOString().slice(0, 10);
              const isToday = dateStr === todayStr;

              // O güne ait ödevler ve canlı dersler
              const dayAssignments = assignments.filter(a => a.due_date && a.due_date.slice(0, 10) === dateStr);
              const daySessions = liveSessions.filter(s => s.scheduled_at && s.scheduled_at.slice(0, 10) === dateStr);
              const totalItems = dayAssignments.length + daySessions.length;

              return (
                <div
                  key={dateStr}
                  className={`flex flex-col rounded-2xl border p-3 min-h-[220px] transition-all ${
                    isToday
                      ? 'border-accent-500 bg-accent-500/5 shadow-sm'
                      : 'border-border bg-surface'
                  }`}
                >
                  {/* Gün Başlığı */}
                  <div className="border-b border-border/60 pb-2 mb-2 flex items-center justify-between">
                    <div>
                      <p className={`text-[11px] font-black uppercase tracking-wider ${isToday ? 'text-accent-600 dark:text-accent-400' : 'text-text-secondary'}`}>
                        {DAY_NAMES[idx]}
                      </p>
                      <p className={`text-xs font-extrabold ${isToday ? 'text-accent-600 dark:text-accent-400' : 'text-text-primary'}`}>
                        {dayDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                    {isToday && (
                      <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-accent-500 text-white">
                        Bugün
                      </span>
                    )}
                  </div>

                  {/* Günün Görevleri */}
                  <div className="space-y-2 flex-1">
                    {totalItems === 0 ? (
                      <p className="text-[11px] text-text-muted italic text-center py-6">Görev yok</p>
                    ) : (
                      <>
                        {/* Canlı Dersler */}
                        {daySessions.map(s => (
                          <div
                            key={`s-${s.id}`}
                            className="p-2 rounded-xl bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-xs space-y-1"
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-bold text-[10px] uppercase text-blue-600 dark:text-blue-400 flex items-center gap-1">
                                <Video size={10} /> Canlı Ders
                              </span>
                              <Badge variant={s.status === 'tamamlandi' ? 'success' : 'accent'}>
                                {s.status === 'tamamlandi' ? 'Bitti' : 'Planlandı'}
                              </Badge>
                            </div>
                            <a
                              href={s.meeting_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-bold text-blue-600 hover:underline flex items-center gap-1 text-[11px]"
                            >
                              <ExternalLink size={10} /> Katıl
                            </a>
                          </div>
                        ))}

                        {/* Ödevler */}
                        {dayAssignments.map(a => {
                          const isDone = a.status === 'tamamlandi';
                          const isTest = a.type === 'test';
                          const isVid = a.type === 'video';

                          return (
                            <Card
                              key={`a-${a.id}`}
                              className="p-2.5 space-y-1.5 hover:border-accent-400 transition-all text-xs"
                            >
                              <div className="flex items-center justify-between gap-1">
                                <span className="font-bold text-[10px] text-text-secondary flex items-center gap-1">
                                  {isVid ? <Video size={10} /> : isTest ? <Target size={10} /> : <FileText size={10} />}
                                  {isTest ? 'Test Ödevi' : isVid ? 'Video' : 'Ödev'}
                                </span>
                                <Badge variant={isDone ? 'success' : 'warning'}>
                                  {isDone ? 'Tamam' : '23:59'}
                                </Badge>
                              </div>

                              <p className={`font-bold text-text-primary text-xs line-clamp-2 ${isDone ? 'line-through opacity-60' : ''}`}>
                                {a.title}
                              </p>

                              {classId && (
                                <Link
                                  to={`/sinif/${classId}/odev/${a.id}`}
                                  onClick={onClose}
                                  className="text-[11px] font-bold text-accent-600 hover:underline block pt-0.5"
                                >
                                  Detay Gör →
                                </Link>
                              )}
                            </Card>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
